import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import CustomPdfViewer from '../components/PDFviewer';

const SERVER = process.env.REACT_APP_SERVER;

const labelCellSx = {
    width: 74,
    minWidth: 74,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    wordBreak: 'keep-all',
    lineHeight: 1.35,
    verticalAlign: 'middle',
};

const valueCellSx = {
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    lineHeight: 1.6,
    verticalAlign: 'middle',
};

function isRegistrationStatus(regDateStart, regDateEnd) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(regDateStart);
    start.setHours(0, 0, 0, 0);

    const end = new Date(regDateEnd);
    end.setHours(0, 0, 0, 0);

    return start <= today && end >= today;
}

function normalizeLecture(data) {
    if (Array.isArray(data)) return data;
    if (data) return [data];
    return [];
}

function LectureDetailComponent({ lectureId }) {
    const navigate = useNavigate();
    const [lecture, setLecture] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;

        axios
            .get(`${SERVER}/api/lecture/${lectureId}`)
            .then((res) => {
                if (!mounted) return;
                setLecture(normalizeLecture(res.data));
                setError(null);
            })
            .catch((err) => {
                if (!mounted) return;
                setError('데이터를 불러오지 못했습니다.');
                console.log(err);
            })
            .finally(() => {
                if (mounted) {
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [lectureId]);

    const handleClick = (id, lectureTitle) => {
        navigate('/application', {
            state: { lectureId: id, lectureTitle },
        });
    };

    if (loading) return <div>로딩중...</div>;
    if (error) return <div>{error}</div>;

    return (
        <Box sx={{ width: { xs: '100%', md: '70%' }, mx: 'auto', my: 5, px: { xs: 2, md: 0 } }}>
            {lecture.map((item) => (
                <Paper key={item.id} sx={{ p: { xs: 2.5, md: 3 } }}>
                    <TableContainer>
                        <Table sx={{ tableLayout: 'fixed' }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={labelCellSx}>강의명</TableCell>
                                    <TableCell sx={valueCellSx}>{item.title}</TableCell>
                                    <TableCell sx={labelCellSx}>
                                        신청
                                        <br />
                                        기간
                                    </TableCell>
                                    <TableCell sx={valueCellSx}>
                                        {item.reg_period_start.split('T')[0]} ~ {item.reg_period_end.split('T')[0]}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={labelCellSx}>강사</TableCell>
                                    <TableCell sx={valueCellSx}>{item.instructor_name}</TableCell>
                                    <TableCell sx={labelCellSx}>
                                        강의
                                        <br />
                                        기간
                                    </TableCell>
                                    <TableCell sx={valueCellSx}>
                                        {item.lec_period_start.split('T')[0]} ~ {item.lec_period_end.split('T')[0]}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={labelCellSx}>
                                        주최
                                        <br />
                                        기관
                                    </TableCell>
                                    <TableCell sx={valueCellSx}>{item.organizer}</TableCell>
                                    <TableCell sx={labelCellSx}>문의</TableCell>
                                    <TableCell sx={valueCellSx}>{item.inquiry}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ my: 4 }}>
                        <Typography
                            sx={{ whiteSpace: 'pre-wrap' }}
                            component="div"
                            dangerouslySetInnerHTML={{ __html: item.content || '' }}
                        />
                        {item.file_name && (
                            <Box sx={{ my: 2, width: '90%' }}>
                            {item.file_name.toLowerCase().endsWith('.pdf') ? (
                                <CustomPdfViewer fileUrl={`${SERVER}/api/upload/lectureFiles/${item.file_name}`} />
                            ) : (
                                <img
                                    src={`${SERVER}/api/upload/lectureFiles/${item.file_name}`}
                                    alt={item.file_name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                            )}
                            </Box>
                        )}
                    </Box>

                    {isRegistrationStatus(item.reg_period_start, item.reg_period_end) && item.is_activated === 1 && (
                        <Box textAlign="center" sx={{ mb: 4 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => handleClick(item.id, item.title)}
                            >
                                신청하기
                            </Button>
                        </Box>
                    )}
                </Paper>
            ))}
        </Box>
    );
}

LectureDetailComponent.propTypes = {
    lectureId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default LectureDetailComponent;
